import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, App, Button, Input, InputNumber, Segmented, Select, Switch, Tooltip, Typography } from 'antd';

import EventFormElement from '../events/EventFormElement';
import RecordBadge from '../common/RecordBadge';
import ContextBar from '../common/ContextBar';
import { DataState, Skeleton, deriveDataState } from '../loading';
import FormActionsFooter from '../common/FormActionsFooter';
import { collectionCrumbs } from '../../utils/breadcrumbs';
import { CardOptionLabel, buildMemberCardOptions } from '../cards/CardVisuals';
import { TRANSFORM_LABELS } from '../cards/DrivenFromModal';
import { optionSearchFilter } from '../Input';
import { CHEVRON_DOWN, CHEVRON_UP, PLUS, TRASH } from '../icons';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { MULTI_ADD_SCOPE } from '../../hooks/useMultiAddForms';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { useOptionalPreferences } from '../../contexts/PreferencesContext';
import { newMoneyCurrency } from '../../utils/newMoneyCurrency';
import { transformInfo } from '../../config/elementCoercion';
import AdHocInputEditor, { AD_HOC_INPUT_EXCLUDED_TYPES, adHocInputProblem } from '../common/AdHocInputEditor';
import { getCollectionMemberCardIds } from '../../utils/collections';
import { canManage, canReadCard, canWriteCard } from '../../utils/permissions';
import { mergedElementsForCard } from '../../utils/formulaTokens';
import { normalizeId } from '../../utils/ids';
import { isElementValueEmpty } from '../../utils/elements';
import {
	createMultiAddForm,
	demoteMultiAddForm,
	getMultiAddForm,
	promoteMultiAddForm,
	updateMultiAddForm,
} from '../../services/multiAddForms';
import {
	MAPPING,
	askOnceSuggestions,
	buildFormBody,
	dropInputReferences,
	isDriven,
	labelOf,
	mappingOptions,
	mappingValueOf,
	parseMappingValue,
	pruneIllegalMappings,
	rekeyStepReferences,
	slugify,
	uniqueKey,
	unmappedInputs,
} from './multiAddEditorModel';
import { stepCardsOf } from './MultiAddButtons';
import TemplateTagsField from '../tags/TemplateTagsField';
import { useScreenShortcuts } from '../../contexts/ShortcutsContext';
import { escapeBelongsElsewhere } from '../../utils/escapeOwner';
import useElementChoices from '../../hooks/useElementChoices';
import QueryRefusalPick from './QueryRefusalPick';
import ConditionBuilder from '../common/ConditionBuilder';

// The types an ad hoc input never takes (PR 10b: a file needs an upload;
// KPR-390: a "Me too" count is kept by keepr from the people who confirm —
// keepr-api utils/multiAddForms/validate.js NEVER_INPUT_TYPES). A repeating
// group (KPR-679) is closed here until the item that opens it: a multi add
// input collects one value, not a list of entries.
export const MULTI_ADD_EXCLUDED_TYPES = AD_HOC_INPUT_EXCLUDED_TYPES;

/**
 * The multi add editor — a full page (the mapping table is too large for a
 * modal) in the record-form layout:
 *
 *   Basics   name · who can use it (managers) · "Link the items"
 *   Steps    the cards, in creation order: card, label, required (every
 *            submission adds it; off, the person adding may leave it out),
 *            repeat, move up/down, remove
 *   Inputs   ad hoc inputs: label, type, required, then the same options a
 *            card element of that type takes (config/elementOptionPolicy
 *            getOptionKeysForInput) — help text, choices, lookup card,
 *            measure and units, decimals and leading zeros, the yes/no
 *            labels, min and max, default value, the boolean toggle rows;
 *            an input nothing receives is flagged and blocks Save (the
 *            form is a conduit — a value nobody stores would be typed and
 *            lost)
 *   Fields   one panel per step: every element with its mapping — Ask ·
 *            Skip · Fixed value · From input … · Same as … · The … this
 *            form creates — greyed where the coercion matrix or the lookup
 *            rules refuse it, with a conversion picker and a lossy warning
 *            where a transform applies. "Ask once" links same-named
 *            elements to the earlier step that asks them.
 *
 * New forms are created in the user tier; a manager who picks "Everyone"
 * gets the create followed by a promote (the API's own two steps).
 */

const TIER_OPTIONS = [
	{ value: MULTI_ADD_SCOPE.USER, label: 'Just me' },
	{ value: MULTI_ADD_SCOPE.COLLECTION, label: 'Everyone' },
];

const GROUP_LABELS = { basics: 'Basics', input: 'From an input', element: 'Same as an earlier step', step: 'Created by an earlier step' };

// The step's name as the person adding sees it: its label, else its card's.
const stepLabelOf = (step, cardsById) => step?.label || cardsById?.[normalizeId(step?.card_id)]?.name || step?.key || '';

let uidCounter = 0;
const uid = () => `u${++uidCounter}`;

const blankInput = (taken) => ({ uid: uid(), key: uniqueKey('input', taken), label: '', dataType: 'text-small', options: {} });
const blankStep = (taken) => ({ uid: uid(), key: uniqueKey('step', taken), card_id: '', label: '', required: false, repeat: null, fields: {}, tagIds: [] });

const fromStored = form => ({
	name: form?.name || '',
	linkItems: !!form?.linkItems,
	inputs: (form?.inputs || []).map(input => ({ uid: uid(), key: input.key, label: input.label || '', dataType: input.dataType, options: input.options || {} })),
	steps: (form?.steps || []).map(step => ({
		uid: uid(),
		key: step.key,
		card_id: normalizeId(step.card_id),
		label: step.label || '',
		required: !!step.required,
		repeat: step.repeat ? { min: step.repeat.min ?? 1, max: step.repeat.max ?? 50, label: step.repeat.label || '' } : null,
		seedFrom: step.seedFrom ? {
			card_id: normalizeId(step.seedFrom.card_id) || '',
			kql: step.seedFrom.kql || '',
			max: step.seedFrom.max ?? null,
			fields: step.seedFrom.fields || {},
		} : null,
		fields: step.fields || {},
		tagIds: Array.isArray(step.tagIds) ? step.tagIds : [],
	})),
});

export default function MultiAddEditorPage() {
	const { collectionId, formId } = useParams();
	const isNew = !formId;
	const navigate = useNavigate();
	const { message } = App.useApp();
	const collection = useCollectionContext()?.collection || null;
	// The currency a new money input starts in (ruled L3): the collection's,
	// else the author's own, else US dollars — a collection that sets one is
	// never steered by whoever is editing the form.
	const prefs = useOptionalPreferences()?.prefs || null;
	const newMoneyCode = useMemo(() => newMoneyCurrency({ collection, prefs }), [collection, prefs]);
	const { cards, cardsById, getElementsForCard, loading: cardsLoading } = useCardDefinitions();
	const isManager = canManage(collection);

	const [model, setModel] = useState(() => fromStored(null));
	const [tier, setTier] = useState(MULTI_ADD_SCOPE.USER);
	const [stored, setStored] = useState(null);
	// 'loading' | 'ready' | 'error' (the read failed: Retry) | 'missing' (the
	// API answered that it is not there, or not this person's). Nothing but
	// 'ready' opens the form, so a blank draft is never editable or saveable
	// over the stored multi add (docs/platform-standards.md §14).
	const [loadState, setLoadState] = useState(isNew ? 'ready' : 'loading');
	const [loadAttempt, setLoadAttempt] = useState(0);
	const [nameError, setNameError] = useState(null);
	const [formError, setFormError] = useState(null);
	const [saving, setSaving] = useState(false);
	const savingRef = useRef(false);

	const { isDirty, resetDirty, guardDiscard } = useDirtyForm({ model, tier });
	const resetDirtyRef = useRef(resetDirty);
	useEffect(() => { resetDirtyRef.current = resetDirty; }, [resetDirty]);

	const settingsPath = `/collections/${collectionId}/form?tab=add-item`;
	const ready = loadState === 'ready' && !cardsLoading;

	useEffect(() => {
		if (isNew) { resetDirtyRef.current({ model: fromStored(null), tier: MULTI_ADD_SCOPE.USER }); return undefined; }
		let active = true;
		setLoadState('loading');
		getMultiAddForm(collectionId, formId)
			.then(loaded => {
				if (!active) return;
				const next = fromStored(loaded);
				const scope = loaded?.scope === MULTI_ADD_SCOPE.COLLECTION ? MULTI_ADD_SCOPE.COLLECTION : MULTI_ADD_SCOPE.USER;
				setStored(loaded);
				setModel(next);
				setTier(scope);
				setLoadState('ready');
				resetDirtyRef.current({ model: next, tier: scope });
			})
			.catch(error => {
				if (!active) return;
				// Only an answer is a verdict: 404/403 is a multi add that is not there
				// (or not theirs); no response, or a 5xx, is worth another try.
				setLoadState([403, 404].includes(error?.response?.status) ? 'missing' : 'error');
			});
		return () => { active = false; };
	}, [isNew, collectionId, formId, loadAttempt]);

	// ----- cards -----

	const memberIds = useMemo(() => (collection ? getCollectionMemberCardIds(collection, cards) : new Set()), [collection, cards]);
	const writableCardOptions = useMemo(
		() => buildMemberCardOptions(cards || [], memberIds)
			.filter(opt => !collection || canWriteCard(collection, opt.value))
			.map(opt => ({ ...opt, label: <CardOptionLabel card={opt.card} depth={opt.depth} /> })),
		[cards, memberIds, collection]
	);
	const readableCardOptions = useMemo(
		() => buildMemberCardOptions(cards || [], memberIds)
			.filter(opt => !collection || canReadCard(collection, opt.value))
			.map(opt => ({ ...opt, label: <CardOptionLabel card={opt.card} depth={opt.depth} /> })),
		[cards, memberIds, collection]
	);

	const elementsByStep = useMemo(() => {
		const out = {};
		model.steps.forEach(step => {
			const card = cardsById?.[normalizeId(step.card_id)];
			out[step.key] = card
				? mergedElementsForCard({ ownElements: card.elements || [], parentCardId: card.parentCardId, getElementsForCard }).filter(el => el?.name)
				: [];
		});
		return out;
	}, [model.steps, cardsById, getElementsForCard]);

	// The elements a step's seed filter can ask about: the seed card's own, with
	// the ones it inherits. The filter is about THAT card, not the step's.
	const seedElementsByStep = useMemo(() => {
		const out = {};
		model.steps.forEach(step => {
			const card = step.seedFrom?.card_id ? cardsById?.[normalizeId(step.seedFrom.card_id)] : null;
			out[step.key] = card
				? mergedElementsForCard({ ownElements: card.elements || [], parentCardId: card.parentCardId, getElementsForCard }).filter(el => el?.name)
				: [];
		});
		return out;
	}, [model.steps, cardsById, getElementsForCard]);

	const deadEnds = useMemo(() => unmappedInputs(model.inputs, model.steps), [model.inputs, model.steps]);

	// Whatever changed — a step's card or repeat flag, an input's type or
	// options, the order — mappings the picker would no longer offer are
	// dropped, so Save can never send what the API refuses.
	useEffect(() => {
		if (loadState !== 'ready' || cardsLoading) return;
		const { steps, changed } = pruneIllegalMappings({ steps: model.steps, inputs: model.inputs, cardsById, elementsByStep });
		if (changed) setModel(prev => ({ ...prev, steps }));
	}, [loadState, cardsLoading, model.steps, model.inputs, cardsById, elementsByStep]);
	const stepCards = useMemo(() => stepCardsOf({ steps: model.steps }, cardsById), [model.steps, cardsById]);

	const update = useCallback(patch => setModel(prev => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) })), []);

	// ----- steps -----

	const addStep = () => update(prev => ({ steps: [...prev.steps, blankStep(new Set([...prev.inputs.map(i => i.key), ...prev.steps.map(s => s.key)]))] }));

	const setStepCard = (index, cardId) => update(prev => {
		const step = prev.steps[index];
		const card = cardsById?.[normalizeId(cardId)];
		const taken = new Set([...prev.inputs.map(i => i.key), ...prev.steps.filter((_, i) => i !== index).map(s => s.key)]);
		const key = uniqueKey(slugify(card?.name, 'step'), taken);
		// A new card means new elements: the step's own mappings are reset and
		// every later reference to it re-keyed (element copies dropped — the
		// element names no longer line up).
		let steps = prev.steps.map((s, i) => (i === index ? { ...s, key, card_id: normalizeId(cardId), fields: {} } : s));
		steps = steps.map((s, i) => {
			if (i <= index) return s;
			const fields = {};
			Object.entries(s.fields || {}).forEach(([name, mapping]) => {
				if (mapping?.from === MAPPING.ELEMENT && mapping.step === step.key) return;
				if (mapping?.from === MAPPING.STEP && mapping.step === step.key) { fields[name] = { ...mapping, step: key }; return; }
				fields[name] = mapping;
			});
			return { ...s, fields };
		});
		return { steps };
	});

	const patchStep = (index, patch) => update(prev => ({ steps: prev.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)) }));

	const removeStep = index => update(prev => {
		const key = prev.steps[index].key;
		return { steps: rekeyStepReferences(prev.steps.filter((_, i) => i !== index), key, null) };
	});

	const moveStep = (index, direction) => update(prev => {
		const target = index + direction;
		if (target < 0 || target >= prev.steps.length) return {};
		const steps = [...prev.steps];
		[steps[index], steps[target]] = [steps[target], steps[index]];
		// Order matters: a mapping may only read an EARLIER step. Anything
		// now pointing forward is dropped.
		const keysBefore = new Set();
		const cleaned = steps.map(step => {
			const fields = {};
			Object.entries(step.fields || {}).forEach(([name, mapping]) => {
				if ((mapping?.from === MAPPING.ELEMENT || mapping?.from === MAPPING.STEP) && !keysBefore.has(mapping.step)) return;
				fields[name] = mapping;
			});
			keysBefore.add(step.key);
			return { ...step, fields };
		});
		return { steps: cleaned };
	});

	const setMapping = (stepIndex, element, value) => update(prev => {
		const step = prev.steps[stepIndex];
		const fields = { ...(step.fields || {}) };
		const parsed = parseMappingValue(value);
		if (!parsed) delete fields[element.name];
		else {
			if (parsed.from === MAPPING.INPUT || parsed.from === MAPPING.ELEMENT) {
				const option = mappingOptions({ element, stepIndex, steps: prev.steps, inputs: prev.inputs, cardsById, elementsByStep }).find(o => o.value === value);
				parsed.transform = { type: (option?.transforms || ['none'])[0] };
			}
			fields[element.name] = parsed;
		}
		return { steps: prev.steps.map((s, i) => (i === stepIndex ? { ...s, fields } : s)) };
	});

	const patchMapping = (stepIndex, name, patch) => update(prev => ({
		steps: prev.steps.map((s, i) => (i === stepIndex ? { ...s, fields: { ...s.fields, [name]: { ...s.fields[name], ...patch } } } : s)),
	}));

	const askOnce = stepIndex => {
		const additions = askOnceSuggestions({ stepIndex, steps: model.steps, elementsByStep, cardsById });
		const names = Object.keys(additions);
		if (!names.length) { message.info('Nothing to link: no earlier step asks for the same elements.'); return; }
		update(prev => ({ steps: prev.steps.map((s, i) => (i === stepIndex ? { ...s, fields: { ...s.fields, ...additions } } : s)) }));
		message.success(`Linked ${names.length} element${names.length === 1 ? '' : 's'} to earlier steps.`);
	};

	// ----- inputs -----

	const addInput = () => update(prev => ({ inputs: [...prev.inputs, blankInput(new Set([...prev.inputs.map(i => i.key), ...prev.steps.map(s => s.key)]))] }));
	// One input is edited by AdHocInputEditor, which hands back the whole next
	// input. A new type invalidates every mapping typed against the old one.
	const changeInput = (index, next, { typeChanged = false } = {}) => update(prev => ({
		inputs: prev.inputs.map((input, i) => (i === index ? { ...next, uid: input.uid } : input)),
		...(typeChanged ? { steps: dropInputReferences(prev.steps, prev.inputs[index].key) } : {}),
	}));
	const removeInput = index => update(prev => {
		const key = prev.inputs[index].key;
		return { inputs: prev.inputs.filter((_, i) => i !== index), steps: dropInputReferences(prev.steps, key) };
	});

	// ----- save -----

	const validate = () => {
		if (!model.name.trim()) { setNameError('Give the multi add a name.'); return 'name'; }
		if (!model.steps.length) return 'Add at least one step.';
		for (const step of model.steps) {
			if (!step.card_id) return 'Every step needs a card.';
			for (const [name, mapping] of Object.entries(step.fields || {})) {
				if (mapping?.from !== MAPPING.STATIC) continue;
				const element = (elementsByStep[step.key] || []).find(el => el.name === name);
				if (isElementValueEmpty(element || { dataType: 'text-small' }, mapping.value)) {
					return `${labelOf(element) || name}: a fixed value is blank — pick a value, or switch it to Ask or Skip.`;
				}
			}
		}
		for (const input of model.inputs) {
			const problem = adHocInputProblem(input);
			if (problem) return problem;
		}
		if (deadEnds.length) return `Nothing receives ${deadEnds.map(i => `"${i.label || i.key}"`).join(', ')} — map it to an element or remove it.`;
		return null;
	};

	const moveTier = async (saved, wanted) => {
		const current = saved?.scope === MULTI_ADD_SCOPE.COLLECTION ? MULTI_ADD_SCOPE.COLLECTION : MULTI_ADD_SCOPE.USER;
		if (!isManager || current === wanted) return saved;
		try {
			const id = normalizeId(saved?._id);
			const moved = await (wanted === MULTI_ADD_SCOPE.COLLECTION ? promoteMultiAddForm(collectionId, id) : demoteMultiAddForm(collectionId, id));
			// A form for everyone leaves its maker's private tags behind (T9).
			if (moved?.tagsLeftOff) {
				message.info(`${moved.tagsLeftOff === 1 ? 'Your private tag stays' : 'Your private tags stay'} yours: the multi add for everyone leaves ${moved.tagsLeftOff === 1 ? 'it' : 'them'} off.`);
			}
			return moved || saved;
		} catch (err) {
			message.warning(err?.response?.data?.message || (wanted === MULTI_ADD_SCOPE.COLLECTION
				? 'Saved for you, but it could not be pinned for everyone.'
				: 'Saved, but it could not be unpinned.'));
			return saved;
		}
	};

	// An element a seed filter could mean several of (400
	// `ambiguous_element`, element ids step 3): the person picks one, or any
	// of these cards, and the multi add is saved again with the pick.
	// …answering exactly the seed filters it was made for: any change forgets it.
	const elementPicks = useElementChoices(JSON.stringify((model.steps || []).map(step => step.seedFrom || null)));

	const handleSave = async () => {
		if (!ready) return; // ⌘↵ before the record has loaded must not save the blank draft
		setNameError(null);
		setFormError(null);
		const problem = validate();
		if (problem === 'name') return;
		if (problem) { setFormError(problem); return; }
		if (savingRef.current) return; // ⌘↵ twice must not save twice
		savingRef.current = true;
		setSaving(true);
		try {
			const body = elementPicks.withChoices(buildFormBody(model));
			const saved = isNew
				? await createMultiAddForm(collectionId, body)
				: await updateMultiAddForm(collectionId, formId, body);
			elementPicks.reset();
			await moveTier(saved || stored, tier);
			message.success(isNew ? 'Multi add created.' : 'Multi add saved.');
			resetDirtyRef.current({ model, tier });
			navigate(settingsPath);
		} catch (err) {
			const status = err?.response?.status;
			if (status === 409) setNameError('You already have a multi add with this name');
			else if (!elementPicks.take(err)) setFormError(err?.response?.data?.message || 'Could not save the multi add.');
		} finally {
			savingRef.current = false;
			setSaving(false);
		}
	};

	const handleCancel = () => guardDiscard(() => navigate(settingsPath));

	useScreenShortcuts('Multi add form editor', [
		{ keys: ['mod', 'enter'], label: 'Save the form' },
		{ keys: ['esc'], label: 'Cancel' },
	]);

	const shortcutRef = useRef(null);
	shortcutRef.current = { handleSave, handleCancel };
	useEffect(() => {
		const handler = (event) => {
			if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') { event.preventDefault(); shortcutRef.current.handleSave(); }
			else if (event.key === 'Escape') {
				if (escapeBelongsElsewhere(event)) return;
				event.preventDefault();
				shortcutRef.current.handleCancel();
			}
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, []);

	// ----- render -----

	const title = model.name.trim() || (isNew ? 'New multi add' : 'Multi add');
	// "Adds 3 steps" when every step is required; "Adds up to 3 steps (1
	// required)" once the person adding may leave some out.
	const requiredSteps = model.steps.filter(step => step.required).length;
	const stepCount = `${model.steps.length} step${model.steps.length === 1 ? '' : 's'}`;
	const stepsSummary = requiredSteps === model.steps.length ? `Adds ${stepCount}` : `Adds up to ${stepCount} (${requiredSteps || 'none'} required)`;

	const renderMappingRow = (step, stepIndex, element) => {
		const mapping = step.fields?.[element.name] || null;
		const value = mappingValueOf(mapping);
		const options = mappingOptions({ element, stepIndex, steps: model.steps, inputs: model.inputs, cardsById, elementsByStep });
		const chosen = options.find(o => o.value === value) || null;
		const groups = ['basics', 'input', 'element', 'step']
			.map(group => ({ label: GROUP_LABELS[group], options: options.filter(o => o.group === group).map(o => ({ value: o.value, label: o.label, disabled: !o.ok, title: o.ok ? undefined : o.reason, searchText: o.label.toLowerCase() })) }))
			.filter(group => group.options.length);
		const transforms = chosen?.transforms || [];
		const transform = mapping?.transform?.type || transforms[0] || 'none';
		const info = transform && transform !== 'none' ? transformInfo(transform) : null;
		const card = cardsById?.[normalizeId(step.card_id)];
		const rowId = `multi-add-editor-${step.key}-${element.name}`;
		// Reading an optional step into a required element: when that step is
		// left out the value is blank, and the API refuses the submission.
		const optionalSource = chosen?.sourceOptional && element.options?.required && (mapping?.from === MAPPING.ELEMENT || mapping?.from === MAPPING.STEP)
			? model.steps.find(s => s.key === mapping.step) || null
			: null;
		return (
			<div key={element.name} className={`quick-add-row multi-add-editor__row multi-add-editor__row--${mapping?.from || 'ask'}`} data-element={element.name} data-step={step.key}>
				<div className="quick-add-row__head">
					<span className="quick-add-row__label" id={`${rowId}-label`}>{labelOf(element)}{element.options?.required ? ' *' : ''}</span>
					<Select
						size="small"
						className="multi-add-editor__mapping"
						aria-labelledby={`${rowId}-label`}
						value={value}
						onChange={next => setMapping(stepIndex, element, next)}
						options={groups}
						showSearch
						filterOption={optionSearchFilter}
						popupMatchSelectWidth={false}
						optionRender={option => <Tooltip title={option.data.title} placement="right"><span>{option.data.label}</span></Tooltip>}
					/>
				</div>
				{mapping?.from === MAPPING.STATIC ? (
					<div className="quick-add-row__control quick-add-row__control--fixed">
						<EventFormElement
							event={{ _id: null, collection_id: collectionId, elements: {} }}
							// Scoped per step: the same element name recurs across
							// steps and the inputs derive their ids from it.
							element={{ ...element, name: `${element.name}__ma-${step.key}` }}
							value={mapping.value}
							onChange={(name, next) => patchMapping(stepIndex, String(name).split('__ma-')[0], { value: next })}
							cardMeta={card}
							showIconBadge={false}
						/>
					</div>
				) : null}
				{(mapping?.from === MAPPING.INPUT || mapping?.from === MAPPING.ELEMENT) && transforms.length > 1 ? (
					<div className="quick-add-row__toolbar">
						<Select
							size="small"
							aria-label={`${labelOf(element)} conversion`}
							value={transform}
							onChange={next => patchMapping(stepIndex, element.name, { transform: { type: next } })}
							options={transforms.map(name => ({ value: name, label: TRANSFORM_LABELS[name] || name }))}
							popupMatchSelectWidth={false}
						/>
					</div>
				) : null}
				{info?.lossy ? <Alert type="warning" showIcon message={info.warning} /> : null}
				{info && !info.lossy && transforms.length <= 1 ? (
					<Typography.Text type="secondary" className="d-block">{TRANSFORM_LABELS[transform] || transform}</Typography.Text>
				) : null}
				{optionalSource ? (
					<Typography.Text type="secondary" className="d-block multi-add-editor__optional-note">
						If the {stepLabelOf(optionalSource, cardsById)} step is left out, this required field is empty and the submission is refused.
					</Typography.Text>
				) : null}
			</div>
		);
	};

	// The editor's actions, defined once: the context bar at the top and
	// FormActionsFooter at the end of the main column render the same node.
	const formActions = (
		<>
			<Button onClick={handleCancel}>Cancel</Button>
			<Button type="primary" onClick={handleSave} loading={saving} disabled={!ready || (!isNew && !isDirty)}>
				{isNew ? 'Create multi add' : 'Save changes'}
			</Button>
		</>
	);

	return (
		<div className="item-form-page multi-add-editor">
			<ContextBar
				breadcrumb={collectionCrumbs(collection, collectionId, 'Multi add')}
				records={stepCards}
				kind="card"
				title={title}
				below={model.steps.length ? <span>{stepsSummary}{model.inputs.length ? `, ${model.inputs.length} input${model.inputs.length === 1 ? '' : 's'}` : ''}</span> : null}
				actions={formActions}
			/>

			{formError ? <Alert type="error" showIcon message={formError} role="alert" className="multi-add-editor__error" /> : null}
			<QueryRefusalPick
				inline
				className="multi-add-editor__error"
				refusal={elementPicks.refusal}
				disabled={saving}
				onPick={(value) => { elementPicks.pick(value); handleSave(); }}
				onRepick={() => { elementPicks.clearRefusal(); handleSave(); }}
			/>

			<div className="item-form-columns">
				<div className="item-form-main">
					{!ready ? (
						loadState === 'missing' ? (
							<Alert
								type="error"
								showIcon
								message="That multi add is not available."
								description="It may have been deleted, or belong to someone else."
								action={<Button onClick={() => navigate(settingsPath)}>Go to multi add</Button>}
							/>
						) : (
							<DataState
								state={deriveDataState({
									loading: loadState === 'loading' || (loadState === 'ready' && cardsLoading),
									error: loadState === 'error' ? 'Check your connection and try again.' : null,
								})}
								skeleton={<Skeleton variant="form-field" count={5} />}
								error={loadState === 'error' ? 'Check your connection and try again.' : null}
								onRetry={() => setLoadAttempt(n => n + 1)}
								label="this multi add"
							/>
						)
					) : (
						<>
							<section className="form-panel">
								<div className="micro-label form-panel__kicker">Basics</div>
								<div className="item-form-fields">
									<div className="item-form-field field--half">
										<label htmlFor="multi-add-name" className="d-block">Name</label>
										<Input
											id="multi-add-name"
											value={model.name}
											placeholder="e.g. New client"
											onChange={event => { update({ name: event.target.value }); setNameError(null); }}
											status={nameError ? 'error' : undefined}
										/>
										{nameError ? <div className="saved-filters__name-error" role="alert">{nameError}</div> : null}
									</div>
									{isManager ? (
										<div className="item-form-field field--half">
											<label className="d-block" id="multi-add-tier-label">Who can use it</label>
											<Segmented aria-labelledby="multi-add-tier-label" value={tier} onChange={setTier} options={TIER_OPTIONS} />
										</div>
									) : null}
								</div>
								<div className="toggle-row">
									<div className="toggle-row__text">
										<span className="toggle-row__label">Link the items</span>
										<span className="toggle-row__description">Relate every item this form adds to every other, so each one lists the rest under Related items.</span>
									</div>
									<Switch checked={model.linkItems} onChange={checked => update({ linkItems: checked })} aria-label="Link the items" />
								</div>
							</section>

							<section className="form-panel" data-testid="multi-add-editor-steps">
								<div className="form-panel__kicker-row">
									<div className="micro-label form-panel__kicker">Steps <span className="form-panel__count">{model.steps.length}</span></div>
									<Button size="small" icon={<PLUS />} onClick={addStep} disabled={model.steps.length >= 20}>Add step</Button>
								</div>
								<div className="form-panel__hint">One item per step, created in this order. A later step can reference the items of earlier ones.</div>
								{model.steps.length ? (
									<div className="multi-add-editor__steps">
										{model.steps.map((step, index) => {
											const card = cardsById?.[normalizeId(step.card_id)];
											return (
												<div className="multi-add-editor__step" key={step.uid} data-testid={`multi-add-editor-step-${index}`}>
													<div className="multi-add-editor__step-head">
														<span className="multi-add-editor__step-index">{index + 1}</span>
														<Select
															className="multi-add-editor__card"
															placeholder="Pick a card"
															aria-label={`Step ${index + 1} card`}
															value={step.card_id || undefined}
															onChange={cardId => setStepCard(index, cardId)}
															options={writableCardOptions}
															showSearch
															filterOption={optionSearchFilter}
															popupClassName="card-picker-dropdown"
															style={{ minWidth: 220 }}
														/>
														<Input
															className="multi-add-editor__step-label"
															placeholder={card ? `Label (default: ${card.name})` : 'Label'}
															aria-label={`Step ${index + 1} label`}
															value={step.label}
															onChange={event => patchStep(index, { label: event.target.value })}
														/>
														<span className="multi-add-editor__step-tools">
															<Button type="text" size="small" icon={<CHEVRON_UP />} aria-label={`Move step ${index + 1} up`} disabled={index === 0} onClick={() => moveStep(index, -1)} />
															<Button type="text" size="small" icon={<CHEVRON_DOWN />} aria-label={`Move step ${index + 1} down`} disabled={index === model.steps.length - 1} onClick={() => moveStep(index, 1)} />
															<Button type="text" size="small" icon={<TRASH />} aria-label={`Remove step ${index + 1}`} onClick={() => removeStep(index)} />
														</span>
													</div>
													<div className="multi-add-editor__step-options">
														<label className="multi-add-editor__inline">
															<Switch
																size="small"
																checked={!!step.required}
																// A required step is in every submission, so it cannot
																// repeat 0 times (the API refuses the pair): its floor
																// becomes 1 as it turns required.
																onChange={checked => patchStep(index, {
																	required: checked,
																	...(checked && step.repeat && !(step.repeat.min >= 1) ? { repeat: { ...step.repeat, min: 1 } } : {}),
																})}
																aria-label={`Step ${index + 1} required`}
															/>
															<span>Required</span>
															<span className="multi-add-editor__inline-note">Every submission adds this card. Off: the person adding may leave it out.</span>
														</label>
														<label className="multi-add-editor__inline">
															<Switch
																size="small"
																checked={!!step.repeat}
																onChange={checked => patchStep(index, { repeat: checked ? { min: 1, max: 20, label: '' } : null })}
																aria-label={`Step ${index + 1} repeats`}
															/>
															<span>Repeats (several {card?.name || 'items'} in one submission)</span>
														</label>
														{card ? (
															<TemplateTagsField
																id={`multi-add-step-${index}-tags`}
																collectionId={collectionId}
																collection={collection}
																value={step.tagIds || []}
																onChange={next => patchStep(index, { tagIds: next })}
																allowPrivate={tier !== MULTI_ADD_SCOPE.COLLECTION && !(stored?.scope === MULTI_ADD_SCOPE.COLLECTION)}
																label={`Step ${index + 1} tags`}
																extra={`Put on each ${card?.name || 'item'} this step adds. The person adding sees them first and may leave any off.`}
															/>
														) : null}
														{step.repeat ? (
															<span className="multi-add-editor__repeat">
																<span>from</span>
																<InputNumber size="small" min={step.required ? 1 : 0} max={step.repeat.max} value={step.repeat.min} aria-label={`Step ${index + 1} minimum`} onChange={v => patchStep(index, { repeat: { ...step.repeat, min: Math.max(step.required ? 1 : 0, Number.isFinite(v) ? v : 0) } })} />
																<span>to</span>
																<InputNumber size="small" min={Math.max(1, step.repeat.min)} max={50} value={step.repeat.max} aria-label={`Step ${index + 1} maximum`} onChange={v => patchStep(index, { repeat: { ...step.repeat, max: Number.isFinite(v) ? v : 50 } })} />
																<Input size="small" placeholder="Called…" aria-label={`Step ${index + 1} repeat label`} value={step.repeat.label} onChange={event => patchStep(index, { repeat: { ...step.repeat, label: event.target.value } })} style={{ width: 140 }} />
															</span>
														) : null}
														{/* A repeating step can take its LINES from the data:
														    one per matching item, pre-filled from it. The
														    author says which card and which filter; the form
														    then opens with the work already listed. */}
														{step.repeat ? (
															<div className="multi-add-editor__seed">
																<label>
																	<Switch
																		size="small"
																		checked={!!step.seedFrom}
																		onChange={checked => patchStep(index, {
																			seedFrom: checked ? { card_id: '', kql: '', max: null, fields: {} } : null,
																		})}
																		aria-label={`Step ${index + 1} fills its lines from data`}
																	/>
																	<span>Fill the lines from data</span>
																</label>
																{step.seedFrom ? (<>
																	<Select
																		size="small"
																		style={{ minWidth: 200 }}
																		placeholder="One line per…"
																		aria-label={`Step ${index + 1} seed card`}
																		value={step.seedFrom.card_id || undefined}
																		options={readableCardOptions}
																		onChange={value => patchStep(index, { seedFrom: { ...step.seedFrom, card_id: value, fields: {} } })}
																	/>
																	<div className="multi-add-editor__seed-filter" role="group" aria-label={`Step ${index + 1} seed filter`}>
																		{step.seedFrom.card_id ? (
																			<ConditionBuilder
																				value={step.seedFrom.kql || ''}
																				onChange={value => patchStep(index, { seedFrom: { ...step.seedFrom, kql: value } })}
																				elements={seedElementsByStep[step.key] || []}
																				collectionId={collectionId}
																				// The seed reads as the person filling the form in
																				// (their own read scope), so "Me" is theirs.
																				allowMe
																				emptyText="No conditions yet. Every item of this card gets a line."
																			/>
																		) : (
																			<Typography.Text type="secondary">Pick the card first, then say which of its items count.</Typography.Text>
																		)}
																	</div>
																</>) : null}
															</div>
														) : null}
													</div>
												</div>
											);
										})}
									</div>
								) : (
									<Typography.Text type="secondary">No steps yet — add the first card this form creates.</Typography.Text>
								)}
							</section>

							<section className="form-panel" data-testid="multi-add-editor-inputs">
								<div className="form-panel__kicker-row">
									<div className="micro-label form-panel__kicker">Inputs <span className="form-panel__count">{model.inputs.length}</span></div>
									<Button size="small" icon={<PLUS />} onClick={addInput} disabled={model.inputs.length >= 50}>Add input</Button>
								</div>
								<div className="form-panel__hint">Extra questions on the form, independent of any card — a date or a person asked once and mapped into several steps. Every input must land somewhere.</div>
								{deadEnds.length ? (
									<Alert
										type="error"
										showIcon
										className="multi-add-editor__dead-ends"
										message={`Nothing receives ${deadEnds.map(i => `"${i.label || i.key}"`).join(', ')}`}
										description="Map it to an element in a step below, or remove it — the form never stores a value on its own."
									/>
								) : null}
								{model.inputs.map((input, index) => {
									const dead = deadEnds.includes(input);
									return (
										<AdHocInputEditor
											key={input.uid}
											input={input}
											index={index}
											dead={dead}
											testId={`multi-add-editor-input-${index}`}
											cardOptions={readableCardOptions}
											moneyCode={newMoneyCode}
											// An ad hoc input is never a file (PR 10b): a multi add form
											// uploads nothing.
											excludedTypes={MULTI_ADD_EXCLUDED_TYPES}
											onChange={(next, meta) => changeInput(index, next, meta)}
											onRemove={() => removeInput(index)}
										/>
									);
								})}
							</section>

							{model.steps.map((step, stepIndex) => {
								const card = cardsById?.[normalizeId(step.card_id)];
								if (!card) return null;
								const elements = (elementsByStep[step.key] || []).filter(el => !isDriven(el));
								return (
									<section className="form-panel multi-add-editor__fields" key={step.uid} data-testid={`multi-add-editor-fields-${step.key}`}>
										<div className="form-panel__kicker-row">
											<div className="micro-label form-panel__kicker multi-add-step__kicker">
												<RecordBadge kind="card" record={card} size="xs" title={step.label || card.name} />
												<span>fields</span>
											</div>
											{stepIndex > 0 ? (
												<Tooltip title="Link every element an earlier step already asks for by the same name, so it is asked once.">
													<Button size="small" onClick={() => askOnce(stepIndex)}>Ask once</Button>
												</Tooltip>
											) : null}
										</div>
										{elements.length ? (
											<div className="quick-add-rows">
												{elements.map(element => renderMappingRow(step, stepIndex, element))}
											</div>
										) : (
											<Typography.Text type="secondary">This card has no elements a form can set.</Typography.Text>
										)}
									</section>
								);
							})}

							<FormActionsFooter>{formActions}</FormActionsFooter>
						</>
					)}
				</div>

				<aside className="item-form-side">
					<section className="form-panel">
						<div className="micro-label form-panel__kicker">How it works</div>
						<Typography.Paragraph type="secondary" className="multi-add-editor__help">
							A multi add is one form that adds several items. Each step is one card; its elements are asked on the form unless you map them — to a fixed value, to an input, to the same element of an earlier step, or to the item an earlier step creates. The form stores nothing itself.
						</Typography.Paragraph>
					</section>
					<div className="item-form-shortcuts">
						<span><kbd>⌘↵</kbd> Save</span>
						<span><kbd>Esc</kbd> Cancel</span>
					</div>
				</aside>
			</div>
		</div>
	);
}
